import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'

/**
 * Snacket kring en turnering, per gäng.
 *
 * En widget i skärmens högerkant, inte en spalt i flödet. Ställningen är det
 * sidan finns för; snacket ska gå att fälla fram när man vill ha det och
 * annars inte kosta en enda pixel av bredden. Stängd som utgångsläge, med en
 * prick när det kommit något nytt sedan sist.
 *
 * "Sedan sist" ligger i localStorage och inte i databasen. En läskvitto-
 * tabell hade betytt en skrivning per öppning och en till rad att hålla
 * ren, för en prick. Blir den fel är det värsta som händer att en prick
 * visas i onödan på en ny enhet.
 */
interface ChatRow {
  id: number
  user_id: string
  display_name: string
  body: string
  created_at: string
}

interface Group {
  id: string
  name: string
}

const seenKey = (tournamentId: string, groupId: string) =>
  `mt:chat-seen:${tournamentId}:${groupId}`

export function TournamentChat({ tournamentId }: { tournamentId: string }) {
  const { session } = useAuth()
  const [groups, setGroups] = useState<Group[]>([])
  const [groupId, setGroupId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<ChatRow[]>([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unread, setUnread] = useState(false)

  const listRef = useRef<HTMLDivElement | null>(null)

  // Vilka gäng är jag med i? Nästan alltid ett, men den som är med i flera
  // ska kunna välja tråd i stället för att få den första godtyckligt.
  useEffect(() => {
    async function loadGroups() {
      const uid = session?.user.id
      if (!uid) return

      // RLS på group_members släpper igenom hela gänget, inte bara mina
      // egna rader. Utan filtret på user_id kom en rad per medlem tillbaka
      // och trådväljaren fylldes med samma gäng om och om igen.
      const { data } = await supabase
        .from('group_members')
        .select('groups(id, name)')
        .eq('user_id', uid)
        .order('joined_at')
      // PostgREST typar den inbäddade relationen som en array även när den
      // är en-till-en, så platta ut båda formerna.
      const list = ((data ?? []) as unknown as Array<{ groups: Group | Group[] | null }>)
        .flatMap((r) => (Array.isArray(r.groups) ? r.groups : r.groups ? [r.groups] : []))
      setGroups(list)
      setGroupId((prev) => prev ?? list[0]?.id ?? null)
    }
    void loadGroups()
  }, [session])

  const load = useCallback(async () => {
    if (!groupId) return
    const { data, error } = await supabase
      .from('tournament_chat')
      .select('id, user_id, display_name, body, created_at')
      .eq('tournament_id', tournamentId)
      .eq('group_id', groupId)
      .order('created_at')

    if (error) {
      setError(error.message)
      return
    }

    const list = (data as ChatRow[]) ?? []
    setRows(list)

    const newest = list.at(-1)?.created_at
    const seen = localStorage.getItem(seenKey(tournamentId, groupId))
    setUnread(Boolean(newest) && (!seen || newest! > seen))
  }, [tournamentId, groupId])

  useEffect(() => {
    void load()
  }, [load])

  // Samma push som leaderboarden. Realtime respekterar RLS, så bara det egna
  // gängets inlägg kommer fram — filtret på tournament_id räcker.
  useEffect(() => {
    if (!groupId) return
    const channel = supabase
      .channel(`chat:${tournamentId}:${groupId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournament_messages',
          filter: `tournament_id=eq.${tournamentId}`,
        },
        () => void load(),
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [tournamentId, groupId, load])

  // Kvittera som läst medan panelen står öppen, inte bara när den öppnas —
  // annars ligger pricken kvar på inlägg man just läst.
  useEffect(() => {
    if (!open || !groupId) return
    const newest = rows.at(-1)?.created_at
    if (newest) localStorage.setItem(seenKey(tournamentId, groupId), newest)
    setUnread(false)
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [open, rows, tournamentId, groupId])

  // Escape stänger. Panelen lägger sig över ställningen, och det ska gå att
  // bli av med den utan att först sikta på knappen bakom.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  async function send(e: FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || !groupId || !session) return

    setBusy(true)
    setError(null)
    const { error } = await supabase.from('tournament_messages').insert({
      tournament_id: tournamentId,
      group_id: groupId,
      user_id: session.user.id,
      body,
    })
    setBusy(false)

    if (error) setError(error.message)
    else {
      setDraft('')
      await load()
    }
  }

  async function remove(id: number) {
    const { error } = await supabase.from('tournament_messages').delete().eq('id', id)
    if (error) setError(error.message)
    else await load()
  }

  // Är man inte med i något gäng finns ingen tråd att visa — men att bara
  // försvinna är fel. Panelen såg ut att vara trasig i stället för
  // otillgänglig, och den frågan ställdes två gånger innan orsaken hittades.
  // Knappen står därför kvar och förklaringen ligger inuti panelen, i stället
  // för som en lös textrad svävande i hörnet.
  const noGroup = groups.length === 0

  return (
    <div className={`chat${open ? ' chat--open' : ''}`}>
      <button
        className="chat__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="chat-panel"
      >
        {open ? 'Dölj snacket' : 'Snacket'}
        {!open && rows.length > 0 && <span className="chat__count">{rows.length}</span>}
        {!open && unread && <span className="chat__dot" aria-label="nytt sedan sist" />}
      </button>

      {open && noGroup && (
        <section className="chat__panel" id="chat-panel">
          <p className="muted chat__note">
            Snacket hör till ett gäng. <Link to="/gang">Skapa ett eller gå med</Link> så
            dyker tråden upp här.
          </p>
        </section>
      )}

      {open && !noGroup && (
        <section className="chat__panel" id="chat-panel">
          {groups.length > 1 && (
            <div className="chat__groups">
              <label htmlFor="chat-group">Gäng</label>
              <select
                id="chat-group"
                value={groupId ?? ''}
                onChange={(e) => setGroupId(e.target.value)}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div className="notice notice--error">{error}</div>}

          <div className="chat__list" ref={listRef}>
            {rows.length === 0 && (
              <p className="muted">Ingen har sagt något än. Var först.</p>
            )}

            {rows.map((m) => {
              const mine = m.user_id === session?.user.id
              return (
                <div key={m.id} className={`chat__msg${mine ? ' chat__msg--mine' : ''}`}>
                  <div className="chat__meta">
                    <span className="chat__author">{m.display_name}</span>
                    <span className="chat__time">
                      {new Date(m.created_at).toLocaleTimeString('sv-SE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {mine && (
                      <button
                        className="link chat__remove"
                        onClick={() => void remove(m.id)}
                        aria-label="Ta bort inlägget"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <p className="chat__body">{m.body}</p>
                </div>
              )
            })}
          </div>

          <form className="chat__form" onSubmit={send}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={1000}
              placeholder="Skriv något…"
              aria-label="Nytt inlägg"
            />
            <button className="primary" type="submit" disabled={busy || !draft.trim()}>
              {busy ? '…' : 'Skicka'}
            </button>
          </form>

          <p className="muted chat__note">
            Bara ditt gäng ser det här. Inlägg går inte att redigera — ta bort och
            skriv nytt.
          </p>
        </section>
      )}
    </div>
  )
}
